import { Box, Icon, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { headingFont } from 'theme/tokens';
import type { MethodId } from './MethodologySection';
import FormulaBreakdown, { type FormulaProps } from './FormulaBreakdown';

export default function ResultCard({
  icon,
  label,
  source,
  methodId,
  onSourceClick,
  children,
  highlight,
  formula,
}: {
  icon: React.ElementType;
  label: string;
  source?: string;
  methodId?: MethodId;
  onSourceClick?: (id: MethodId) => void;
  children: React.ReactNode;
  highlight?: boolean;
  formula?: FormulaProps;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      <Box
        bg={highlight ? 'brand.500' : 'white'}
        color={highlight ? 'white' : 'gray.800'}
        border="1px solid"
        borderColor={highlight ? 'brand.500' : 'gray.100'}
        borderRadius="2xl"
        p={8}
        textAlign="center"
        shadow={highlight ? 'xl' : 'sm'}
        transition="all 0.2s"
        _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
        h="100%"
      >
        <Icon
          as={icon}
          boxSize={6}
          color={highlight ? 'whiteAlpha.800' : 'brand.400'}
          mb={3}
        />
        <Text
          fontFamily={headingFont}
          fontWeight="800"
          fontSize={{ base: '2xl', md: '3xl' }}
          lineHeight="1.1"
          mb={2}
        >
          {children}
        </Text>
        <Text
          fontSize="sm"
          fontWeight="500"
          color={highlight ? 'whiteAlpha.800' : 'gray.500'}
        >
          {label}
        </Text>
        {source && (
          <Text
            fontSize="xs"
            fontStyle="italic"
            color={highlight ? 'whiteAlpha.600' : 'gray.400'}
            mt={2}
            cursor={methodId ? 'pointer' : undefined}
            textDecoration={methodId ? 'underline' : undefined}
            textDecorationStyle="dotted"
            _hover={methodId ? { color: highlight ? 'whiteAlpha.800' : 'brand.500' } : undefined}
            onClick={methodId && onSourceClick ? () => onSourceClick(methodId) : undefined}
            role={methodId ? 'button' : undefined}
            tabIndex={methodId ? 0 : undefined}
            onKeyDown={
              methodId && onSourceClick
                ? (e) => { if (e.key === 'Enter') onSourceClick(methodId); }
                : undefined
            }
          >
            {source}
          </Text>
        )}
        {formula && <FormulaBreakdown {...formula} onDark={highlight} />}
      </Box>
    </motion.div>
  );
}
